import * as React from 'react';
import { Component } from 'react';
import { observable, action, computed, reaction, isArrayLike } from 'mobx';
import { observer, inject } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import MediaQuery from 'react-responsive';
import _ShareclassRows from './ShareclassRows';
import _ShareclassCards from './ShareclassCards';
import withShareclass from './withShareclass';
import Column from './Column';
import GroupedColumn from './GroupedColumn';
import { Table, TableHead, TableBody, TableRow, ColumnHeader } from './Grid';
import Translate from './Translate';
import get from 'lodash/get';
import ColumnHeaderExpander from './ColumnHeaderExpander';
import FundGroupHeader from './FundGroupHeader';
import { getColumnLabel } from '../utils/getColumnLabel';
import { IQueryContext } from '@kurtosys/ksys-api-client/dist/models/commonTypes';
import getQueryResults from '../utils/query/getQueryResults';
import { DocumentCategory } from '../models/configuration/documents/DocumentCategory';

const ShareclassRows = withShareclass(_ShareclassRows);
const ShareclassCards = withShareclass(_ShareclassCards);

const FundListWrapper = styled.section``;

const Cards = styled<any, any>('div')`
    ${propStyles};
`;

Cards.defaultProps = {};

const ColumnHeaderInner = styled<any, any>('div')`
    ${propStyles};
`;

ColumnHeaderInner.defaultProps = {
    display: 'flex',
    alignItems: 'center',
};

const ColumnLabel = styled<any, any>('div')`
    ${propStyles};
`;

ColumnLabel.defaultProps = {
    flex: '1 1 auto',
    textAlign: 'center',
};

@inject('dataStore')
@observer
class Fund extends Component<any, any> {
    @observable documentDownloads = [];
    @observable expanded = true;

    constructor(props) {
        super(props);

        this.setDocumentDownloads();

        reaction(
            () => [
                props.dataStore.groupedFunds,
                props.dataStore.documentPropertySearch,
                props.dataStore.documentFilterSearch,
            ],
            data => {
                this.setDocumentDownloads();
            },
            // TODO: Find the correct solution
            // Without a delay setDocumentDownloads seems to use the previous state
            // E.g. this.shareClasses is the previous group of share classes
            { delay: 1 }
        );
    }

    @computed
    get shareClasses() {
        const shareClasses = this.props.shareClasses.map(shareClass => {
            // If we have any property searches, filter the documents
            const documents = shareClass.documents.filter(document =>
                this.props.dataStore.documentPropertySearch.length > 0
                    ? this.documentPropertyFilter(document)
                    : document
            );

            // Return shareClass with filtered documents
            return {
                ...shareClass,
                ...{
                    documents,
                },
            };
        });

        return shareClasses;
    }

    @action
    setDocumentDownloads = () => {
        // Get array of all shareclass documents
        // If there are active document filters, filter documents by meta values
        const { dataStore } = this.props;
        const { rowConfig, entityType } = dataStore.config;

        const allDocuments = this.shareClasses.reduce(
            (accumulator, shareClass) => {
                // filter out documents that are not showing due to categories not being configured.
                let result = [];
                if (this.hasDocumentCategories) {
                    const { documentCategories } = rowConfig;
                    for (const documentCategory of documentCategories as Array<
                        DocumentCategory
                    >) {
                        const queryContext: IQueryContext = {
                            entityByType: {
                                [entityType]: shareClass,
                            },
                        };
                        const documentCategoryQuery = {
                            documentGroup: documentCategory.groupBy,
                        };
                        const documentData = getQueryResults(
                            queryContext,
                            documentCategoryQuery,
                            dataStore.culture
                        );
                        result.push(...documentData.documentGroup);
                    }
                } else {
                    result = shareClass.documents;
                }
                // fall back to all shareclass documents if document categories are not configured.
                return accumulator.concat(
                    result.filter(document =>
                        this.props.dataStore.documentFilterSearch.length > 0
                            ? this.documentMetaFilter(document)
                            : document
                    )
                );
            },
            []
        );

        // Set documentDownloads as array of unique documents
        this.documentDownloads = allDocuments.filter(
            (document, index, filterArray) =>
                filterArray.findIndex(
                    filterDocument =>
                        filterDocument.clientCode === document.clientCode
                ) === index
        );
    };

    @computed
    get hasDocumentCategories() {
        const { rowConfig } = this.props.dataStore.config;
        const { documentCategories } = rowConfig;
        return documentCategories && documentCategories.length > 0;
    }

    // Filters documents based on document meta
    // Loop over document filters and get the value for specified meta (property),
    // then return if that value is included in the filter values
    documentMetaFilter = document => {
        const { dataStore } = this.props;
        const { documentFilterSearch } = dataStore;
        for (const filter of documentFilterSearch) {
            const documentMetaValue = get(
                document,
                `meta.${filter.property}.value`
            );
            if (documentMetaValue) {
                let isDocMetaValid;
                if (isArrayLike(documentMetaValue)) {
                    isDocMetaValid = filter.values.some(v =>
                        documentMetaValue.includes(v)
                    );
                } else {
                    isDocMetaValid = filter.values.includes(documentMetaValue);
                }
                if (!isDocMetaValid) {
                    return false;
                }
            }
        }
        return true;
    };

    // Filters documents based on document properties
    // Loop over document property searches and get the value of any matching document property,
    // then check if that value is included in the filter values
    documentPropertyFilter = document => {
        for (const filter of this.props.dataStore.documentPropertySearch) {
            const documentProperty = get(document, filter.property);
            if (documentProperty) {
                return filter.values.includes(documentProperty);
            }
        }
    };

    @computed
    get downloadCount() {
        return this.documentDownloads.length;
    }

    @computed
    get rowHasExpandableOptions() {
        return Object.keys(this.props.dataStore.config.rowConfig).length > 0
            ? true
            : false;
    }

    @computed
    get rowExpanderConfig() {
        const { expander } = this.props.dataStore.config.rowConfig;
        return expander;
    }

    @computed
    get rowType() {
        const { type } = this.props.dataStore.config.rowConfig;
        return type;
    }

    @computed
    get hasDropdownOrGroup() {
        const { columns } = this.props;
        return columns.some(column =>
            ['dropdown', 'group'].includes(column.type)
        );
    }

    @computed
    get rowSpan() {
        return this.hasDropdownOrGroup ? 2 : 1;
    }

    @action
    toggleDownload = document => {
        const checked =
            this.documentDownloads.findIndex(
                documentDownload =>
                    documentDownload.clientCode === document.clientCode
            ) > -1;

        if (checked) {
            this.removeDownload(document);
        } else {
            this.addDownload(document);
        }
    };

    @action
    addDownload = document => {
        this.documentDownloads.push(document);
    };

    @action
    removeDownload = document => {
        this.documentDownloads = this.documentDownloads.filter(
            documentDownload =>
                documentDownload.clientCode !== document.clientCode
        );
    };

    @action
    toggleExpanded = () => {
        const { shareClasses } = this;
        const { dataStore } = this.props;
        // If the header is being collapsed then collapse shareclasses
        if (this.expanded) {
            for (const shareClass of shareClasses) {
                dataStore.setExpandStatus(shareClass, this.expanded);
            }
        }
        this.expanded = !this.expanded;
    };

    @computed
    get mediaQuerySize() {
        const { dataStore, theme } = this.props;
        const switchToCardView = dataStore && dataStore.switchToCardView;
        return theme.breakpoints[switchToCardView];
    }
    render() {
        const {
            expanded,
            toggleExpanded,
            documentDownloads,
            downloadCount,
            toggleDownload,
            rowHasExpandableOptions,
            rowExpanderConfig,
            rowType,
            shareClasses,
            mediaQuerySize,
            rowSpan,
            hasDropdownOrGroup,
        } = this;
        const {
            theme,
            dataStore,
            fundName,
            parentFundName,
            grouped,
            columns,
            children,
            isSubFund,
            subGroupedFund,
        } = this.props;
        return (
            <FundListWrapper>
                {grouped && (
                    <FundGroupHeader
                        fundName={fundName}
                        parentFundName={parentFundName}
                        shareClasses={shareClasses}
                        rowType={rowType}
                        expanded={expanded}
                        toggleExpanded={toggleExpanded}
                        downloadCount={downloadCount}
                        documentDownloads={documentDownloads}
                        isSubFund={isSubFund}
                        subGroupedFund={subGroupedFund}
                    />
                )}
                {!children && expanded && (
                    <MediaQuery query={`(min-width: ${mediaQuerySize})`}>
                        {matches =>
                            matches ? (
                                <Table {...theme.FundListTable}>
                                    <TableHead
                                        {...{
                                            ...theme.ColumnList,
                                            ...theme.FundListTableHead,
                                        }}
                                    >
                                        <TableRow
                                            {...theme.FundListTableHeadRow}
                                        >
                                            {rowHasExpandableOptions &&
                                                (!rowExpanderConfig ||
                                                    rowExpanderConfig.align ===
                                                        'left') && (
                                                    <ColumnHeaderExpander
                                                        rowSpan={rowSpan}
                                                        showColumnHeader={
                                                            rowExpanderConfig &&
                                                            rowExpanderConfig.showColumnHeader
                                                        }
                                                    />
                                                )}
                                            {columns.map((column, index) => {
                                                const columnLabel = getColumnLabel(
                                                    column,
                                                    dataStore
                                                );
                                                if (
                                                    column.type ===
                                                        'watchlist' &&
                                                    dataStore.watchListEnabled
                                                ) {
                                                    return (
                                                        <ColumnHeader
                                                            rowSpan={rowSpan}
                                                            key={`${columnLabel}-${index}`}
                                                            paddingX="0"
                                                            {...theme.ColumnHeader}
                                                            width={
                                                                column.width ||
                                                                '50px'
                                                            }
                                                            verticalAlign={
                                                                column.verticalAlign
                                                            }
                                                            textAlign={
                                                                column.textAlign ||
                                                                'center'
                                                            }
                                                        >
                                                            {column.showLabel !==
                                                                false && (
                                                                <ColumnHeaderInner
                                                                    {...theme.ColumnHeaderInner}
                                                                >
                                                                    <ColumnLabel
                                                                        {...theme.ColumnLabel}
                                                                        textAlign={
                                                                            column.textAlign ||
                                                                            'center'
                                                                        }
                                                                    >
                                                                        <Translate>
                                                                            {
                                                                                columnLabel
                                                                            }
                                                                        </Translate>
                                                                    </ColumnLabel>
                                                                </ColumnHeaderInner>
                                                            )}
                                                        </ColumnHeader>
                                                    );
                                                }

                                                if (
                                                    [
                                                        'group',
                                                        'dropdown',
                                                    ].includes(column.type)
                                                ) {
                                                    return (
                                                        <GroupedColumn
                                                            key={`${columnLabel}-${index}`}
                                                            column={column}
                                                            columnIndex={index}
                                                            setSelectedGroup={
                                                                dataStore
                                                                    .columnStore
                                                                    .setSelectedGroup
                                                            }
                                                            selectedGroupMap={
                                                                dataStore
                                                                    .columnStore
                                                                    .selectedGroupMap
                                                            }
                                                            groupedColumnType="GroupedHeader"
                                                            columnStore={
                                                                dataStore.columnStore
                                                            }
                                                        />
                                                    );
                                                }

                                                return (
                                                    <Column
                                                        key={`${columnLabel}-${index}`}
                                                        column={column}
                                                        rowSpan={rowSpan}
                                                    />
                                                );
                                            })}
                                            {rowHasExpandableOptions &&
                                                rowExpanderConfig &&
                                                rowExpanderConfig.align ===
                                                    'right' && (
                                                    <ColumnHeaderExpander
                                                        rowSpan={rowSpan}
                                                        showColumnHeader={
                                                            rowExpanderConfig &&
                                                            rowExpanderConfig.showColumnHeader
                                                        }
                                                    />
                                                )}
                                        </TableRow>
                                        {hasDropdownOrGroup && (
                                            <TableRow
                                                {...theme.FundListTableHeadSubRow}
                                            >
                                                {columns.map(
                                                    (column, index) => {
                                                        const columnLabel = getColumnLabel(
                                                            column,
                                                            dataStore
                                                        );
                                                        if (
                                                            [
                                                                'group',
                                                                'dropdown',
                                                            ].includes(
                                                                column.type
                                                            )
                                                        ) {
                                                            return (
                                                                <GroupedColumn
                                                                    key={`${columnLabel}-${index}`}
                                                                    column={
                                                                        column
                                                                    }
                                                                    columnIndex={
                                                                        index
                                                                    }
                                                                    setSelectedGroup={
                                                                        dataStore
                                                                            .columnStore
                                                                            .setSelectedGroup
                                                                    }
                                                                    selectedGroupMap={
                                                                        dataStore
                                                                            .columnStore
                                                                            .selectedGroupMap
                                                                    }
                                                                    groupedColumnType="GroupedSubHeader"
                                                                    columnStore={
                                                                        dataStore.columnStore
                                                                    }
                                                                />
                                                            );
                                                        }
                                                    }
                                                )}
                                            </TableRow>
                                        )}
                                    </TableHead>
                                    <TableBody {...theme.FundListTableBody}>
                                        {shareClasses.map(
                                            (shareClass, index) => (
                                                <ShareclassRows
                                                    key={`row-${index}`}
                                                    isMobile={!matches}
                                                    shareClass={shareClass}
                                                    columns={columns}
                                                    selectedGroupMap={
                                                        dataStore.columnStore
                                                            .selectedGroupMap
                                                    }
                                                    documentDownloads={
                                                        documentDownloads
                                                    }
                                                    toggleDownload={
                                                        toggleDownload
                                                    }
                                                    alternated={
                                                        index % 2 ? true : false
                                                    }
                                                    rowHasExpandableOptions={
                                                        rowHasExpandableOptions
                                                    }
                                                    rowExpanderConfig={
                                                        rowExpanderConfig
                                                    }
                                                    rowType={rowType}
                                                    dataStore={dataStore}
                                                />
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Cards>
                                    {shareClasses.map((shareClass, index) => (
                                        <ShareclassCards
                                            key={`card-${index}`}
                                            isMobile={!matches}
                                            shareClass={shareClass}
                                            columns={columns}
                                            selectedGroupMap={
                                                dataStore.columnStore
                                                    .selectedGroupMap
                                            }
                                            setSelectedGroup={
                                                dataStore.columnStore
                                                    .setSelectedGroup
                                            }
                                            documentDownloads={
                                                documentDownloads
                                            }
                                            toggleDownload={toggleDownload}
                                            alternated={
                                                index % 2 ? true : false
                                            }
                                            rowHasExpandableOptions={
                                                rowHasExpandableOptions
                                            }
                                            rowExpanderConfig={
                                                rowExpanderConfig
                                            }
                                            rowType={rowType}
                                            dataStore={dataStore}
                                        />
                                    ))}
                                </Cards>
                            )
                        }
                    </MediaQuery>
                )}
                {expanded && children}
            </FundListWrapper>
        );
    }
}

export default withTheme(Fund) as any;



// WEBPACK FOOTER //
// ./src/Components/Fund.tsx